import { useQuery } from '@tanstack/react-query';
import { ListChecks, Plus, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { type TaskListItem, type TaskPriority } from '@/lib/api-client';
import { cn } from '@/lib/utils';

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  low: 'text-[var(--fg-muted)]',
  normal: 'text-[var(--fg)]',
  high: 'text-amber-500',
  urgent: 'text-red-500',
};

function formatRelativeFatal(iso: string | null): {
  text: string;
  cls: string;
} {
  if (!iso) return { text: '—', cls: 'text-[var(--fg-muted)]' };
  const target = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = target - now;
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const dateStr = new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  if (days < 0)
    return {
      text: `${dateStr} (vencido)`,
      cls: 'text-red-600 font-semibold',
    };
  if (days <= 3) return { text: dateStr, cls: 'text-red-500 font-medium' };
  if (days <= 7) return { text: dateStr, cls: 'text-amber-600' };
  return { text: dateStr, cls: 'text-[var(--fg)]' };
}

interface TaskListInlineProps {
  /**
   * Função que retorna a lista. Aceita formatos do api-client (array
   * direto vindo de casesListTasks/clientsListTasks/intimacoesListTasks
   * ou objeto {items, total} vindo de tasksList).
   */
  query: () => Promise<TaskListItem[] | { items: TaskListItem[]; total?: number }>;
  /** Chave única para o cache do TanStack Query. */
  queryKey: readonly unknown[];
  onNewTask: () => void;
  title?: string;
  emptyMessage?: string;
  maxRows?: number;
}

/**
 * Componente reusável de lista de tarefas inline (para abas em outras
 * páginas: detalhe de processo, cliente, intimação).
 *
 * Mostra uma tabela compacta com pin, status, título+categoria, prazo
 * fatal (com urgência colorida), avatares dos responsáveis. Clicar numa
 * linha navega para /escritorio/atividades?taskId=<id> abrindo o drawer
 * da task.
 */
export function TaskListInline({
  query,
  queryKey,
  onNewTask,
  title = 'Tarefas',
  emptyMessage = 'Nenhuma tarefa vinculada ainda.',
  maxRows = 10,
}: TaskListInlineProps) {
  const navigate = useNavigate();

  const q = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await query();
      const items = Array.isArray(res) ? res : res.items;
      return items;
    },
  });

  const items = (q.data ?? []).slice(0, maxRows);

  function openTask(id: string) {
    navigate(`/escritorio/atividades?taskId=${encodeURIComponent(id)}`);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--fg)]">{title}</h3>
        <button
          type="button"
          onClick={onNewTask}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-3.5 w-3.5" />
          Nova tarefa
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)]">
        {q.isLoading && (
          <div className="space-y-2 p-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-8 animate-pulse rounded bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]"
              />
            ))}
          </div>
        )}

        {!q.isLoading && items.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
            <ListChecks
              className="h-8 w-8 text-[var(--fg-muted)] opacity-40"
              strokeWidth={1.5}
            />
            <p className="max-w-xs text-xs text-[var(--fg-muted)]">
              {emptyMessage}
            </p>
            <button
              type="button"
              onClick={onNewTask}
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
            >
              <Plus className="h-3 w-3" />
              Criar primeira tarefa
            </button>
          </div>
        )}

        {!q.isLoading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-[var(--fg-muted)] text-[11px] uppercase">
              <tr>
                <th className="px-2 py-2 w-7" />
                <th className="px-2 py-2 w-5" />
                <th className="px-2 py-2 text-left">Tarefa</th>
                <th className="px-2 py-2 text-left w-36">Prazo fatal</th>
                <th className="px-2 py-2 text-left w-32">Responsáveis</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => {
                const isDone = t.status === 'completed';
                const fatal = formatRelativeFatal(t.fatalDate);
                return (
                  <tr
                    key={t.id}
                    onClick={() => openTask(t.id)}
                    className={cn(
                      'cursor-pointer border-t border-[var(--border)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]',
                      isDone && 'opacity-60',
                    )}
                  >
                    <td className="px-2 py-2 text-center">
                      {t.pinned && (
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500 inline" />
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <span
                        className={cn(
                          'inline-block h-2 w-2 rounded-full',
                          isDone
                            ? 'bg-emerald-500'
                            : t.status === 'in_progress'
                              ? 'bg-blue-500'
                              : 'bg-slate-300',
                        )}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {t.category && (
                          <span
                            className="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium text-white"
                            style={{ background: t.category.color }}
                          >
                            {t.category.name}
                          </span>
                        )}
                        <span
                          className={cn(
                            'font-medium text-sm',
                            PRIORITY_COLOR[t.priority],
                            isDone && 'line-through',
                          )}
                        >
                          {t.title}
                        </span>
                      </div>
                    </td>
                    <td className={cn('px-2 py-2 text-xs', fatal.cls)}>
                      {fatal.text}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex -space-x-1">
                        {t.assignees.slice(0, 3).map((a) => (
                          <span
                            key={a.id}
                            title={a.name}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--surface)] text-[10px] font-medium text-[var(--fg)] ring-2 ring-[var(--card-bg)]"
                          >
                            {a.name.slice(0, 1).toUpperCase()}
                          </span>
                        ))}
                        {t.assignees.length > 3 && (
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[color-mix(in_oklch,var(--fg)_10%,transparent)] text-[10px] font-medium text-[var(--fg-muted)] ring-2 ring-[var(--card-bg)]"
                            title={t.assignees
                              .slice(3)
                              .map((a) => a.name)
                              .join(', ')}
                          >
                            +{t.assignees.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!q.isLoading && (q.data?.length ?? 0) > maxRows && (
          <div className="border-t border-[var(--border)] px-3 py-2 text-center text-[11px] text-[var(--fg-muted)]">
            Mostrando {maxRows} de {q.data?.length}.{' '}
            <a
              href="/escritorio/atividades"
              className="text-blue-600 hover:underline"
            >
              Ver todas
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
